import { useFeedValues } from "lib/context/FeedContext";
import useGetNewsFeed from "core/feed/useNews";
import { parseCookies, setCookie } from "nookies";
import { ReactElement, UIEvent, useEffect, useRef } from "react";
import { Flex } from "@chakra-ui/react";
import TextWithBullet from "components/TextWithBullet";
import CenteredLoader from "components/CenteredLoader";
import { NewsFeedArticle } from "../types";
import EmptyJobsIcon from "lib/svgs/seagull/EmptyJobs";
import { useAppValues } from "lib/context/AppContext";
import { ScreenSizes } from "core/home/types";
import { NewsFeedHeader } from "./NewsFeedHeader";
import styled from "styled-components";
import { NewsFeedCard } from "./NewsFeedCard";
import Text, { TextType } from "components/Text";

interface StyleProps {
  isPhone: boolean;
}
const Container = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  overflow-y: scroll;
  ${({ isPhone }) => `
    padding-left: ${isPhone ? "4%" : "0px"};
    padding-right: ${isPhone ? "4%" : "0px"};
    padding-top: ${isPhone ? "40px" : "96px"};
  `}
`;

// const ResultsRow = styled.div<StyleProps>`
//   display: flex;
//   width: 100%;
//   max-width: 820px;
//   justify-content: flex-start;
//   ${({ isPhone }) => `
//   margin-bottom: ${isPhone ? "12px" : "24px"};
//   `}
// `;

const LoaderContainer = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${({ isPhone }) => `
    padding-top: ${isPhone ? "40px" : "96px"};
  `}
`;

const LoadingMoreContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 40px;
  width: 80%;
  justify-content: center;
  align-items: center;
`;

const FeedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1000px;
  margin-bottom: 80px;
  gap: 33px;
`;

const StyledErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1000px;
  justify-content: center;
  align-items: center;
  margin-bottom: 60px;
`;

const ErrorBulletsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

interface OwnProps {
  xToken?: string | null;
  initialData: NewsFeedArticle[] | null;
  initialHeader?: number | null;
}

export default function NewsListWrapper({
  xToken,
  initialData,
  initialHeader,
}: OwnProps): JSX.Element {
  const { userRefId } = parseCookies(null);

  const { setFeedValues, getFeedValues } = useFeedValues();
  const { feed, query } = getFeedValues();
  const { getAppValues } = useAppValues();
  const { userRefId: appRefId, screenSize } = getAppValues();
  const isPhone =
    screenSize === ScreenSizes.XS || screenSize === ScreenSizes.SM;
  const currentNewsLength = useRef<number>(0);

  const {
    feed: news,
    isLoading,
    size,
    setSize,
    hasMore,
    // total,
  } = useGetNewsFeed({
    // Note - dont put initialData in if there is a search , messes things up
    feed: feed ?? initialData,
    initialHeader: initialHeader ?? null,
    query: query ?? "",
    setFeedValues,
    userRefId: userRefId ? String(userRefId) : null,
    xToken,
  });
  const initialLoad = (!xToken && !userRefId) || (isLoading && size <= 1);
  const stillLoading = initialLoad || isLoading || (!feed?.length && !query);

  useEffect(() => {
    if (
      !isLoading &&
      news &&
      news.length > 0 &&
      news.length > currentNewsLength.current
    ) {
      currentNewsLength.current = news.length;
    }
    if (news && currentNewsLength.current > news?.length) {
      currentNewsLength.current = news.length;
    }
  }, [isLoading, news?.length, currentNewsLength]);

  useEffect(() => {
    if (appRefId && !userRefId) {
      setCookie(null, "userRefId", appRefId, {});
    }
  }, [appRefId, xToken]);

  const handleScroll = (event: UIEvent<HTMLDivElement> | undefined) => {
    const target = event?.nativeEvent.target as HTMLDivElement;
    const atBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    if (
      !isLoading &&
      atBottom &&
      hasMore &&
      news &&
      news?.length > 0 &&
      news.length === currentNewsLength.current
    ) {
      setSize(size + 1);
    }
  };

  return (
    <Container isPhone={isPhone} onScroll={handleScroll}>
      <NewsFeedHeader />
      {/* {Boolean(total) && (
        <ResultsRow isPhone={isPhone}>
          <Text type={TextType.PARAGRAPH}>{total} Results</Text>
        </ResultsRow>
      )} */}
      {initialLoad && (
        <LoaderContainer isPhone={isPhone}>
          <CenteredLoader noMargin />
        </LoaderContainer>
      )}
      {(news ?? []) && (
        <FeedWrapper>
          {(news ?? []).map((article: NewsFeedArticle, index: number) => {
            return (
              <NewsFeedCard
                key={`${article.ref_id}-${index}`}
                article={article}
                userRefId={userRefId ?? null}
              />
            );
          })}
          {Boolean(size && !initialLoad && stillLoading) && (
            <LoadingMoreContainer>
              <CenteredLoader />
            </LoadingMoreContainer>
          )}
        </FeedWrapper>
      )}
      {Boolean(!stillLoading && !feed?.length) && <ErrorContainer />}
    </Container>
  );
}

function ErrorContainer(): ReactElement {
  return (
    <StyledErrorContainer>
      <EmptyJobsIcon />
      <Text
        style={{ marginBottom: "24px", marginTop: "24px" }}
        type={TextType.HEADING_MEDIUM}
      >
        Sorry, no matching news found.
      </Text>
      <ErrorBulletsContainer>
        <TextWithBullet text="Make sure words are spelled correctly" />
        <TextWithBullet text="Try different keywords" />
        <TextWithBullet text="Try more general keywords" />
      </ErrorBulletsContainer>
      <Flex flexDirection="column" justifyContent="flex-start"></Flex>
    </StyledErrorContainer>
  );
}
